import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { calendarUpsertSchema, type CalendarUpsertSchema } from '@repo/schemas/meeting/calendarUpsert';
import { addHours, isValid, roundToNearestMinutes } from 'date-fns';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { DEFAULT_TIME_STEP } from '@/utils/calendar';

interface UseMeetingFormProps {
  open: boolean;
  formData?: Partial<CalendarUpsertSchema>;
  defaultValues?: Partial<CalendarUpsertSchema>;
}

const DEFAULT_FORM_VALUES: Partial<CalendarUpsertSchema> = {
  name: '',
  desc: '',
  type: 'remote',
  users: [],
  groups: [],
  location: null,
  meetingURL: null,
};

export const DEFAULT_EVENT_DURATION_HOURS = 1;
function buildMeetingDefaults(defaultValues?: Partial<CalendarUpsertSchema>, formData?: Partial<CalendarUpsertSchema>): Partial<CalendarUpsertSchema> {
  const merged: Partial<CalendarUpsertSchema> = {
    ...DEFAULT_FORM_VALUES,
    ...defaultValues,
    ...formData,
  };

  const isValidStartDate = isValid(new Date(merged.startDate ?? ''));
  const isValidEndDate = isValid(new Date(merged.endDate ?? ''));

  if (!isValidStartDate) {
    merged.startDate = new Date().toISOString();
  }
  if (!isValidEndDate) {
    merged.endDate = merged.startDate ? addHours(new Date(merged.startDate), DEFAULT_EVENT_DURATION_HOURS).toISOString() : addHours(new Date(), DEFAULT_EVENT_DURATION_HOURS).toISOString();
  }

  merged.startDate = roundToNearestMinutes(new Date(merged.startDate!), { roundingMethod: 'ceil', nearestTo: DEFAULT_TIME_STEP }).toISOString();
  merged.endDate = roundToNearestMinutes(new Date(merged.endDate!), { roundingMethod: 'ceil', nearestTo: DEFAULT_TIME_STEP }).toISOString();

  return merged;
}

export function useMeetingForm(useMeetingFormProps: UseMeetingFormProps) {
  const { open, formData, defaultValues } = useMeetingFormProps;

  const form = useForm<CalendarUpsertSchema>({
    mode: 'onBlur',
    defaultValues: buildMeetingDefaults(defaultValues, formData),
    resolver: standardSchemaResolver(calendarUpsertSchema),
  });

  const method = useWatch({ control: form.control });
  const groups = useWatch({
    control: form.control,
    name: 'groups',
    defaultValue: formData?.groups ?? [],
  });
  const users = useWatch({
    control: form.control,
    name: 'users',
    defaultValue: formData?.users ?? [],
  });
  const startDate = useWatch({
    control: form.control,
    name: 'startDate',
  });
  const endDate = useWatch({
    control: form.control,
    name: 'endDate',
  });

  // Reset form when dialog opens/closes or data changes
  useEffect(() => {
    if (!open) return;

    const mergedDefaults = buildMeetingDefaults(defaultValues, formData);

    form.reset(mergedDefaults);
  }, [open, defaultValues, formData, form]);

  return {
    form,
    method,
    groups,
    users,
    startDate,
    endDate,
  };
}
